import { useEffect, useMemo, useState } from "react";
import { 
  FaEye, 
  FaEdit, 
  FaReceipt, 
  FaMoneyBillWave, 
  FaCalendarAlt, 
  FaTags, 
  FaChartLine, 
  FaChevronDown, 
  FaDownload, 
  FaPlus,
  FaBuilding,
  FaCarSide,
  FaFileInvoiceDollar,
  FaUserTie,
  FaHeartbeat,
  FaPaperPlane,
  FaTools,
  FaGlobe,
  FaSearch
} from "react-icons/fa";
import client from "../api/client";
import "./ExpenseManagement.css";

const expenseCategories = [
  "Stationery",
  "Tape",
  "Printer Repair",
  "Auto Fare / Travel",
  "First Aid / Medical",
  "Team Treat",
  "CA Fees",
  "Office Maintenance",
  "Internet Bills",
  "Miscellaneous Expenses"
];

const categoryIcons = {
  "Stationery": <FaTags />,
  "Tape": <FaTags />,
  "Printer Repair": <FaTools />,
  "Auto Fare / Travel": <FaCarSide />,
  "First Aid / Medical": <FaHeartbeat />,
  "Team Treat": <FaUserTie />,
  "CA Fees": <FaFileInvoiceDollar />,
  "Office Maintenance": <FaBuilding />,
  "Internet Bills": <FaGlobe />,
  "Miscellaneous Expenses": <FaPaperPlane />
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0
  }).format(Number(value || 0));
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getLastMonthDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date;
};

export default function ExpenseManagement() {
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [viewMode, setViewMode] = useState("dashboard");
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Filters for list view
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("date-desc");
  
  // Form data
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    amount: "",
    expense_date: new Date().toISOString().split("T")[0],
    paid_to: "",
    description: "",
    receipt: null
  });
  const [receiptPreview, setReceiptPreview] = useState(null);
  const expenseTitleOptions = useMemo(
    () => [...new Set(expenses.map((expense) => String(expense.title || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [expenses]
  );

  const loadExpenseData = async () => {
    setLoading(true);
    try {
      const [expensesRes, summaryRes, analyticsRes] = await Promise.all([
        client.get("/expenses"),
        client.get("/expenses/summary"),
        client.get("/expenses/analytics")
      ]);
      setExpenses(expensesRes.data || []);
      setSummary(summaryRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error("Load expense data failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenseData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataObj = new FormData();
    
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== undefined) {
        if (key === "receipt" && formData[key] instanceof File) {
          formDataObj.append("receipt", formData[key]);
        } else if (key !== "receipt") {
          formDataObj.append(key, formData[key]);
        }
      }
    });

    try {
      if (editMode && selectedExpense) {
        await client.put(`/expenses/${selectedExpense.id}`, formDataObj);
      } else {
        await client.post("/expenses", formDataObj);
      }
      setViewMode("list");
      setEditMode(false);
      setSelectedExpense(null);
      setFormData({
        title: "",
        category: "",
        amount: "",
        expense_date: new Date().toISOString().split("T")[0],
        paid_to: "",
        description: "",
        receipt: null
      });
      setReceiptPreview(null);
      await loadExpenseData();
    } catch (err) {
      console.error("Submit expense failed:", err);
    }
  };

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setEditMode(true);
    setViewMode("add");
    setFormData({
      title: expense.title || "",
      category: expense.category || "",
      amount: expense.amount || "",
      expense_date: expense.expense_date ? new Date(expense.expense_date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
      paid_to: expense.paid_to || "",
      description: expense.description || "",
      receipt: null
    });
    setReceiptPreview(expense.receipt_file_url || null);
  };

  const handleView = (expense) => {
    setSelectedExpense(expense);
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, receipt: file });
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => setReceiptPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  // Apply filters and sorting to expenses list
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = !searchTerm || 
      expense.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (expense.description && expense.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !filterCategory || expense.category === filterCategory;
    const matchesDateFrom = !dateFrom || new Date(expense.expense_date) >= new Date(dateFrom);
    const matchesDateTo = !dateTo || new Date(expense.expense_date) <= new Date(dateTo);
    return matchesSearch && matchesCategory && matchesDateFrom && matchesDateTo;
  }).sort((a, b) => {
    switch (sortBy) {
      case "date-desc":
        return new Date(b.expense_date) - new Date(a.expense_date);
      case "date-asc":
        return new Date(a.expense_date) - new Date(b.expense_date);
      case "amount-desc":
        return Number(b.amount) - Number(a.amount);
      case "amount-asc":
        return Number(a.amount) - Number(b.amount);
      default:
        return 0;
    }
  });

  // Calculate KPI values for dashboard
  const lastMonthExpenses = analytics?.monthlyTrend?.find(m => {
    const lastMonth = getLastMonthDate();
    const lastMonthStr = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}`;
    return m.month === lastMonthStr;
  })?.total || 0;
  
  const averageExpense = analytics?.categoryWise?.length 
    ? filteredExpenses.reduce((sum, e) => sum + Number(e.amount), 0) / filteredExpenses.length 
    : 0;
    
  const recent7DaysExpenses = expenses.filter(e => {
    const date = new Date(e.expense_date);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return date >= sevenDaysAgo;
  }).reduce((sum, e) => sum + Number(e.amount), 0);

  return (
    <div className="expense-management-page">
      <header className="expense-header">
        <div className="expense-title-block">
          <div className="expense-logo">
            <FaReceipt />
          </div>
          <div>
            <h1>Expense Management</h1>
            <p>Track day-to-day business expenses & identify unnecessary spending</p>
          </div>
        </div>
        {viewMode !== "add" && (
          <button
            type="button"
            className="expense-form-btn primary"
            onClick={() => {
              setViewMode("add");
              setEditMode(false);
              setSelectedExpense(null);
              setFormData({
                title: "",
                category: "",
                amount: "",
                expense_date: new Date().toISOString().split("T")[0],
                paid_to: "",
                description: "",
                receipt: null
              });
              setReceiptPreview(null);
            }}
          >
            <FaPlus style={{ marginRight: "6px" }} /> Add Expense
          </button>
        )}
      </header>

      <div className="expense-view-tabs" aria-label="Expense sections">
        <button
          type="button"
          className={viewMode === "dashboard" ? "active" : ""}
          onClick={() => setViewMode("dashboard")}
        >
          <FaChartLine style={{ marginRight: "6px" }} /> Dashboard
        </button>
        <button
          type="button"
          className={viewMode === "list" ? "active" : ""}
          onClick={() => setViewMode("list")}
        >
          <FaReceipt style={{ marginRight: "6px" }} /> All Expenses
        </button>
        <button
          type="button"
          className={viewMode === "analytics" ? "active" : ""}
          onClick={() => setViewMode("analytics")}
        >
          <FaChartLine style={{ marginRight: "6px" }} /> Analytics
        </button>
      </div>

      {/* --- Dashboard View --- */}
      {viewMode === "dashboard" && summary && (
        <>
          {/* KPI Cards */}
          <div className="expense-kpi-grid">
            <div className="expense-kpi-card">
              <div className="expense-kpi-icon blue">
                <FaMoneyBillWave />
              </div>
              <div className="expense-kpi-content">
                <div className="expense-kpi-label">Total Expenses</div>
                <div className="expense-kpi-value">{formatCurrency(summary.totalExpenses)}</div>
              </div>
            </div>
            
            <div className="expense-kpi-card">
              <div className="expense-kpi-icon green">
                <FaCalendarAlt />
              </div>
              <div className="expense-kpi-content">
                <div className="expense-kpi-label">This Month</div>
                <div className="expense-kpi-value">{formatCurrency(summary.thisMonthExpenses)}</div>
              </div>
            </div>
            
            <div className="expense-kpi-card">
              <div className="expense-kpi-icon purple">
                <FaCalendarAlt />
              </div>
              <div className="expense-kpi-content">
                <div className="expense-kpi-label">Last Month</div>
                <div className="expense-kpi-value">{formatCurrency(lastMonthExpenses)}</div>
              </div>
            </div>
            
            <div className="expense-kpi-card">
              <div className="expense-kpi-icon orange">
                <FaTags />
              </div>
              <div className="expense-kpi-content">
                <div className="expense-kpi-label">Top Category</div>
                <div className="expense-kpi-value" style={{ fontSize: "18px" }}>
                  {summary.topCategory?.category || "No Data"}
                </div>
              </div>
            </div>
            
            <div className="expense-kpi-card">
              <div className="expense-kpi-icon pink">
                <FaReceipt />
              </div>
              <div className="expense-kpi-content">
                <div className="expense-kpi-label">Last 7 Days</div>
                <div className="expense-kpi-value">{formatCurrency(recent7DaysExpenses)}</div>
              </div>
            </div>
            
            <div className="expense-kpi-card">
              <div className="expense-kpi-icon blue">
                <FaFileInvoiceDollar />
              </div>
              <div className="expense-kpi-content">
                <div className="expense-kpi-label">Average Expense</div>
                <div className="expense-kpi-value">{formatCurrency(averageExpense)}</div>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className="expense-dashboard-grid">
            {/* Left column - Monthly Trend + Category Breakdown */}
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <div className="expense-card">
                <div className="expense-card-header">
                  <h3><FaCalendarAlt style={{ color: "#3b82f6" }} /> Monthly Expense Trend</h3>
                </div>
                <div className="expense-chart-container">
                  {analytics?.monthlyTrend?.length ? (
                    <div className="expense-category-list">
                      {[...analytics.monthlyTrend].reverse().slice(-6).map((item, index) => (
                        <div key={item.month || index} className="expense-category-item">
                          <div className="expense-category-info">
                            <div className="expense-category-rank">{(index + 1)}</div>
                            <div className="expense-category-name">{item.month}</div>
                          </div>
                          <div className="expense-category-amount">{formatCurrency(item.total)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="expense-chart-placeholder">No monthly data yet</div>
                  )}
                </div>
              </div>

              <div className="expense-card">
                <div className="expense-card-header">
                  <h3><FaReceipt style={{ color: "#16a34a" }} /> Recent Expenses</h3>
                </div>
                <div className="expense-recent-list">
                  {filteredExpenses.slice(0, 6).map(expense => (
                    <div key={expense.id} className="expense-recent-item">
                      <div className="expense-recent-left">
                        <div className="expense-recent-icon">
                          {categoryIcons[expense.category] || <FaReceipt />}
                        </div>
                        <div className="expense-recent-text">
                          <h4>{expense.title}</h4>
                          <p>{expense.category} • {formatDate(expense.expense_date)}</p>
                        </div>
                      </div>
                      <div className="expense-recent-amount">{formatCurrency(expense.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Right column - Top Categories */}
            <div className="expense-card">
              <div className="expense-card-header">
                <h3><FaTags style={{ color: "#7c3aed" }} /> Top Expense Categories</h3>
              </div>
              <div className="expense-category-list">
                {analytics?.categoryWise?.length ? (
                  analytics.categoryWise.slice(0, 6).map((item, index) => (
                    <div key={item.category || index} className="expense-category-item">
                      <div className="expense-category-info">
                        <div className="expense-category-rank">{(index + 1)}</div>
                        <div className="expense-category-name">{item.category}</div>
                      </div>
                      <div className="expense-category-amount">{formatCurrency(item.total)}</div>
                    </div>
                  ))
                ) : (
                  <div className="expense-chart-placeholder">No category data yet</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* --- Add/Edit Expense View --- */}
      {viewMode === "add" && (
        <div className="expense-form-card">
          <h2 style={{ margin: "0 0 20px", fontSize: "22px", fontWeight: "900", color: "#0f172a" }}>
            {editMode ? "Edit Expense" : "Add New Expense"}
          </h2>
          <form onSubmit={handleSubmit} className="expense-form-grid">
            <div className="expense-form-field">
              <label>Expense Title</label>
              <input
                type="text"
                list="existing-expense-titles"
                placeholder="Select existing or write new expense title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
              <datalist id="existing-expense-titles">
                {expenseTitleOptions.map((title) => <option key={title} value={title} />)}
              </datalist>
            </div>

            <div className="expense-form-field">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                <option value="">Select Category</option>
                {expenseCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="expense-form-field">
              <label>Amount</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
            </div>

            <div className="expense-form-field">
              <label>Expense Date</label>
              <input
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                required
              />
            </div>

            <div className="expense-form-field">
              <label>Paid To</label>
              <input
                type="text"
                value={formData.paid_to}
                onChange={(e) => setFormData({ ...formData, paid_to: e.target.value })}
              />
            </div>

            <div className="expense-form-field">
              <label>Bill / Receipt</label>
              <label className="expense-receipt-upload">
                <input
                  type="file"
                  onChange={handleFileChange}
                />
                {receiptPreview ? (
                  <div>
                    {receiptPreview.startsWith("data:image") ? (
                      <img src={receiptPreview} alt="Receipt" style={{ maxWidth: "200px", maxHeight: "100px", borderRadius: "8px" }} />
                    ) : (
                      <p>PDF / Document Uploaded</p>
                    )}
                    <p style={{ fontSize: "12px", color: "#64748b" }}>Change receipt</p>
                  </div>
                ) : (
                  <div>
                    <FaReceipt style={{ fontSize: "24px", color: "#94a3b8" }} />
                    <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
                      Click to upload receipt (Image / PDF)
                    </p>
                  </div>
                )}
              </label>
            </div>

            <div className="expense-form-field full">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
              />
            </div>

            <div className="expense-form-actions full">
              <button
                type="button"
                className="expense-form-btn secondary"
                onClick={() => {
                  setViewMode("dashboard");
                  setEditMode(false);
                  setSelectedExpense(null);
                  setReceiptPreview(null);
                }}
              >
                Cancel
              </button>
              <button type="submit" className="expense-form-btn primary">
                {editMode ? "Update Expense" : "Save Expense"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* --- Expense List View --- */}
      {viewMode === "list" && (
        <div className="expense-table-section">
          <div className="expense-table-header">
            <h2>All Expenses</h2>
            <div className="expense-table-filters">
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="">All Categories</option>
                {expenseCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="date-desc">Date: Newest First</option>
                <option value="date-asc">Date: Oldest First</option>
                <option value="amount-desc">Amount: High to Low</option>
                <option value="amount-asc">Amount: Low to High</option>
              </select>
            </div>
          </div>
          <div className="expense-table-wrap">
            <table className="expense-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Paid To</th>
                  <th>Created By</th>
                  <th>Receipt</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                      Loading expenses...
                    </td>
                  </tr>
                ) : filteredExpenses.length ? (
                  filteredExpenses.map((expense) => (
                    <tr key={expense.id}>
                      <td>{formatDate(expense.expense_date)}</td>
                      <td style={{ fontWeight: "800" }}>{expense.title}</td>
                      <td>{expense.category}</td>
                      <td style={{ fontWeight: "900" }}>{formatCurrency(expense.amount)}</td>
                      <td>{expense.paid_to || "-"}</td>
                      <td>{expense.created_by_name || "Unknown"}</td>
                      <td>
                        {expense.receipt_file_url ? (
                          <a
                            href={expense.receipt_file_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: "flex", gap: "6px", alignItems: "center", color: "#1d4ed8", textDecoration: "none", fontWeight: "700", fontSize: "13px" }}
                          >
                            <FaReceipt /> View
                          </a>
                        ) : "-"}
                      </td>
                      <td>
                        <div className="expense-actions">
                          <button
                            type="button"
                            className="expense-action-btn view"
                            onClick={() => handleView(expense)}
                          >
                            <FaEye /> View
                          </button>
                          <button
                            type="button"
                            className="expense-action-btn edit"
                            onClick={() => handleEdit(expense)}
                          >
                            <FaEdit /> Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                      No expenses found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- Analytics View --- */}
      {viewMode === "analytics" && analytics && (
        <>
          <div className="expense-analytics-grid">
            <div className="expense-card">
              <div className="expense-card-header">
                <h3><FaCalendarAlt style={{ color: "#3b82f6" }} /> Monthly Trend (Last 12 Months)</h3>
              </div>
              <div className="expense-category-list">
                {analytics.monthlyTrend.map((item, index) => (
                  <div key={item.month || index} className="expense-category-item">
                    <div className="expense-category-info">
                      <div className="expense-category-rank">{(index + 1)}</div>
                      <div className="expense-category-name">{item.month}</div>
                    </div>
                    <div className="expense-category-amount">{formatCurrency(item.total)}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="expense-card">
              <div className="expense-card-header">
                <h3><FaTags style={{ color: "#7c3aed" }} /> Category Wise Spending</h3>
              </div>
              <div className="expense-category-list">
                {analytics.categoryWise.map((item, index) => (
                  <div key={item.category || index} className="expense-category-item">
                    <div className="expense-category-info">
                      <div className="expense-category-rank">{(index + 1)}</div>
                      <div className="expense-category-name">{item.category}</div>
                    </div>
                    <div className="expense-category-amount">{formatCurrency(item.total)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="expense-card">
            <div className="expense-card-header">
              <h3><FaReceipt style={{ color: "#16a34a" }} /> Highest Expenses</h3>
            </div>
            <div className="expense-recent-list">
              {[...expenses].sort((a, b) => Number(b.amount) - Number(a.amount)).slice(0, 5).map(expense => (
                <div key={expense.id} className="expense-recent-item">
                  <div className="expense-recent-left">
                    <div className="expense-recent-icon">
                      {categoryIcons[expense.category] || <FaReceipt />}
                    </div>
                    <div className="expense-recent-text">
                      <h4>{expense.title}</h4>
                      <p>{expense.category} • {formatDate(expense.expense_date)}</p>
                    </div>
                  </div>
                  <div className="expense-recent-amount">{formatCurrency(expense.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* --- Expense View Modal --- */}
      {selectedExpense && viewMode !== "add" && (
        <div className="expense-detail-overlay" onClick={() => setSelectedExpense(null)}>
          <div className="expense-detail-panel" onClick={(e) => e.stopPropagation()}>
            <div className="expense-detail-header">
              <div>
                <h2>{selectedExpense.title}</h2>
                <span>{formatDate(selectedExpense.expense_date)}</span>
              </div>
              <button
                type="button"
                className="expense-close-button"
                onClick={() => setSelectedExpense(null)}
              >
                ×
              </button>
            </div>
            
            <div className="expense-detail-amount">
              <div className="expense-detail-amount-icon">
                {categoryIcons[selectedExpense.category] || <FaReceipt />}
              </div>
              <div className="expense-detail-amount-text">
                <span>Amount</span>
                <strong>{formatCurrency(selectedExpense.amount)}</strong>
              </div>
            </div>
            
            <div className="expense-detail-body">
              <div className="expense-detail-group">
                <h3>Expense Details</h3>
                <div className="expense-detail-metrics">
                  <div className="expense-detail-metric">
                    <span>Category</span>
                    <strong>{selectedExpense.category}</strong>
                  </div>
                  {selectedExpense.paid_to && (
                    <div className="expense-detail-metric">
                      <span>Paid To</span>
                      <strong>{selectedExpense.paid_to}</strong>
                    </div>
                  )}
                  <div className="expense-detail-metric">
                    <span>Created By</span>
                    <strong>{selectedExpense.created_by_name || "Unknown"}</strong>
                  </div>
                  {selectedExpense.description && (
                    <div className="expense-detail-metric" style={{ flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                      <span>Description</span>
                      <strong>{selectedExpense.description}</strong>
                    </div>
                  )}
                  {selectedExpense.receipt_file_url && (
                    <div className="expense-detail-metric" style={{ flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                      <span>Receipt</span>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <a
                          href={selectedExpense.receipt_file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="expense-action-btn view"
                        >
                          <FaEye /> View
                        </a>
                        <a
                          href={selectedExpense.receipt_file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="expense-action-btn edit"
                          download={selectedExpense.receipt_original_name}
                        >
                          <FaDownload /> Download
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="expense-detail-footer">
              <button
                type="button"
                className="expense-form-btn primary"
                style={{ width: "100%" }}
                onClick={() => handleEdit(selectedExpense)}
              >
                <FaEdit style={{ marginRight: "6px" }} /> Edit Expense
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
